import React from 'react';
import { NextPage } from 'next';
import { LegalPageWrapper } from '@/components/legal/LegalPageWrapper';
import { BaseLink } from '@/components/ui/BaseLink';
import { EMAILS } from '@/constants/Strings';

const tabLinks = [
  {
    active: true,
    title: 'Terms of Use - General',
    href: '/terms-general',
  },
  {
    title: 'Terms of Use - Sender',
    href: '/terms-sender',
  },
];

export const TermsGeneral: NextPage = () => (
  <LegalPageWrapper title="General Terms" lastUpdatedAt="December 16, 2021" links={tabLinks}>
    <article>
      <h2>
        <strong>Welcome to Snappy!</strong>
      </h2>
      <p>
        The Website and Services (each as defined below) are provided to you (“<strong>you</strong>
        ”, "<strong>your</strong>" or “<strong>User</strong>”) by Snappy App, Inc. (referred herein
        as "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>" and "
        <strong>Snappy</strong>"). These Snappy General Terms of Use (“<strong>Terms</strong>”)
        govern your use of the Snappy app or the Snappy website located at snappygifts.com, or any
        apps or websites accessible therefrom (collectively, the “<strong>Website</strong>”) and the
        services provided through the Website, or any use thereof (the “<strong>Services</strong>”).
        &nbsp;In addition to these Terms, the Services may be governed by additional agreements or
        such other terms as may be issued by Snappy from time to time (“Services Terms”).
        &nbsp;These Services Terms shall be in addition to, and not in lieu of, these Terms;
        provided, however, that in the event of any conflict between these Terms and the Services
        Terms, the Services Terms shall govern, but only with respect to the specific Services in
        question. &nbsp;
      </p>
      <p>
        Please read these Terms carefully as well as our privacy policy incorporated herein by
        reference, a current version of which is available at
        https://www.snappygifts.com/privacy-policy (“<strong>Privacy Policy</strong>”). Please
        review our Privacy Policy for more information about how we collect, use, disclose and
        otherwise process information about you.
      </p>
      <p>
        By signing up to the Snappy Account (as defined below), accessing the Website, Services, or
        by otherwise clicking to accept these Terms and the Privacy Policy, you represent and
        warrant that you have read and understood the Terms and Privacy Policy, you agree to be
        bound by the Terms, and you acknowledge that these Terms constitute a binding and
        enforceable legal contract between you and Snappy. If you do not agree to the Terms and
        Privacy Policy, you should stop using the Website and Services immediately.
      </p>
      <p>
        We reserve the right, at our sole direction, to change or modify portions or revise the
        Terms at any time without notice. You should periodically visit these Terms to review the
        current terms that apply to your use of the Website or Services. Any use of the Website or
        Services by you after our publication of such revised Terms shall constitute your acceptance
        of these Terms as modified. &nbsp;If we do this, we will post the changes on this page and
        will indicate at the top of this page the date these Terms were last revised. You may read a
        current, effective copy of these Terms at any time by selecting the “Snappy General Terms of
        Use” link on the Website or within the Services. &nbsp;We may also notify you of any
        material changes either through a pop-up notice, e-mail or through other reasonable means.
        Your continued use of the Website or Services after any such changes constitutes your
        acceptance of the new Terms. You should periodically visit this page to review the current
        Terms so you are aware of any revision to which you are bound. If you do not agree to abide
        by this or any future Terms, do not use or access (or continue to use or access) the Website
        or the Services.<strong>‍</strong>
      </p>
      <p>
        <strong>
          ARBITRATION NOTICE AND CLASS ACTION WAIVER: EXCEPT FOR CERTAIN TYPES OF DISPUTES DESCRIBED
          IN THE ARBITRATION SECTION BELOW OR WHERE PROHIBITED BY LAW, YOU AGREE THAT DISPUTES
          BETWEEN YOU AND US WILL BE RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR
          RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.{' '}
        </strong>
        ‍
      </p>
      <section>
        <h3>1. Registration of a User Account</h3>
        <p>
          You may register for an account with the Service (an “<strong>Account</strong>”). Your
          Account is for your sole, personal use. You may not authorize others to use your account,
          and you may not assign or otherwise transfer your account to any other person or entity.
          You may not share your account with another person. You must ensure that all registration
          information you provide will always be valid, correct, and up to date.
        </p>
        <p>
          We may permanently or temporarily suspend or terminate your Account, or otherwise refuse
          you to access the Website or Services without notice and liability, if, in our sole
          determination, the information you provided is revealed to be untrue, inaccurate, or
          incomplete, or if you violate any of these Terms. Upon termination of your Account for any
          reason, you continue to be bound by the provisions of these Terms that are intended to
          survive termination.{' '}
        </p>
        <p>
          Do not share your Account details with others or allow others to access or use your
          Account. You are responsible for the security of your password and will be solely liable
          for any use or unauthorized use under such password. &nbsp;
          <strong>
            We will treat all acts performed through your Account as being performed by you
          </strong>
          . You therefore accept responsibility for all acts done using your Account, whether or not
          authorized by you, including any payment order through any payment instrument (for
          example, credit card or PayPal), and you understand you may be held liable for losses
          incurred by us caused by someone else using your Account. If you become aware of any
          actual or suspected loss, theft, fraud, or unauthorized use of your Account or Account
          password, please contact us immediately at{' '}
          <a href={`mailto:${EMAILS.SUPPORT}`}>{EMAILS.SUPPORT}</a>.
        </p>
        <p>
          You hereby consent to receive electronic communications from us (including, inter alia,
          via email), including notices about your Account, such as password changes and information
          related to transactions. You agree that any notices, agreements, disclosures or other
          communications that we send to you, either electronically by email, pop-up, phone, text or
          by posting notices on the Website or Services, or in any other way, will satisfy any legal
          requirements, including, inter alia, that such communications are in writing. You hereby
          acknowledge that it is your sole responsibility to maintain copies of any electronic
          communications from us or with us by printing a paper copy or saving a digital copy.
        </p>
        <p>
          We may also send you promotional communications via email, SMS, push notifications on the
          App or Website or any other electronic means, including, but not limited to, newsletters,
          special offers, surveys and other information we think might be of interest to you. You
          will have an option to opt out of receiving the promotional communications. To learn more,
          please visit our <BaseLink href="/privacy-policy">Privacy Policy</BaseLink>.
        </p>
        <p>
          <strong>International Users.</strong> &nbsp;The Website and Services are controlled and
          operated within the United States and certain Services may only be intended for use inside
          the United States. You are hereby prohibited from accessing or using the website from
          anywhere the Website, Services or any of the features, functionality, tools, or content
          thereof, is illegal. If you choose to access the Website or Services from a location
          outside the United States, you do so at your own risk and you are solely responsible for
          compliance with applicable laws, rules and regulations, including export laws and any
          regulations and local laws regarding online conduct and content.<strong>‍</strong>
        </p>
      </section>
      <section>
        <h3>2. User's Representations</h3>
        <p>By using the Website and/or Services, you represent and warrant that:</p>
        <ol>
          <li>
            <strong>
              you have reached the age of 18 and the age of majority both in the territory you
              reside in and in the territory of your citizenship;
            </strong>
          </li>
          <li>you are not impersonating any other person;</li>
          <li>
            you use the Website and/or Services only for purposes that are permitted by (i) the
            Terms and (ii) any applicable law, regulation or generally accepted practices or
            guidelines in the relevant jurisdictions, and your use of the Website and/or Services
            does not violate any applicable law or regulation or any obligation;
          </li>
          <li>
            you will use reasonable efforts to prevent unauthorized access to or use of the Website
            and/or Services;
          </li>
          <li>
            you will not engage in any activity which might be perceived as a fraud; &nbsp; &nbsp;{' '}
          </li>
          <li>
            you will not provide any false, inaccurate, or misleading information to any of the
            users of the Website or Services, or to Snappy.
          </li>
          <li>
            your use of the Website and/or Services is at your sole risk; you are solely responsible
            for any breach of your obligations under the Terms and for the consequences, including
            any loss or damage which Snappy may suffer of any such breach;
          </li>
          <li>
            you may be exposed to content that you may find objectionable and that, in this respect,
            you use the Website and Services at your own risk; &nbsp; &nbsp;{' '}
          </li>
          <li>
            you will not post or upload information to the Website and/or Services, or use Website
            and/or Services in connection with material, which a reasonable person or user could
            deem to be offensive, abusive, obscene, objectionable, defamatory, libelous, sexually
            explicit, threatening, advocating harassment or intimidation, racially or ethnically
            offensive or otherwise offensive to any group or individual, or promoting hatred or
            physical harm of any kind;
          </li>
          <li>
            you will not violate any right of publicity, or other right of any person or entity, or
            any law or contractual obligation; including without limiting, rights pursuant to data
            protection, anti-SPAM and privacy laws and regulations;
          </li>
          <li>
            you will upload and disseminate only content or information that you own all required
            rights to under applicable laws, are authorized to disseminate (including not being
            subject to any confidentiality obligations), and do so only consistent with applicable
            laws and as permitted by any agreements to which you are bound;
          </li>
          <li>you will not use the Website and/or Services for a commercial activity;</li>
          <li>
            you will not use the Website and/or Services in any manner which introduces, or may be
            used to introduce, any security vulnerability or any malicious code, virus, Trojan
            horse, or any harmful software, that may compromise, impair or limit the integrity or
            performance of the Website and/or Services, the Snappy infrastructure or operations,
            those of Snappy's affiliates or partners, or any device, software, hardware or network;
          </li>
          <li>
            You will not decipher, decompile, disassemble, or reverse-engineer any software used to
            provide the Website and/or Services;
          </li>
          <li>
            You will not circumvent, disable, or otherwise interfere with security-related features
            of the Website and/or Services or features that prevent or restrict use or copying of
            any content; or engage in any activity that interferes with or disrupts the Website
            and/or Services (or the servers and networks which are connected to the Website and/or
            Services);
          </li>
          <li>
            You will not use any robot, spider, site search or retrieval service, or any other
            manual or automatic device or process to retrieve, index, data-mine, or in any way
            reproduce or circumvent the navigational structure or presentation of the Website and/or
            Services;
          </li>
          <li>
            You will not harvest, collect or mine information about Users of the Website and/or
            Services; conduct of electronic spamming or otherwise distribute any unsolicited or
            unauthorized advertising, promotional or marketing material, or conduct in any similar
            way;
          </li>
          <li>
            You will not reproduce, duplicate, copy, sell, trade or resell the Website and/or
            Services, or any content of the Website and/or Services, or any content of other users,
            for any purpose, without the express written permission by Snappy; and
          </li>
          <li>
            You understand that Snappy can use third parties (e.g., hosting partners) to provide the
            necessary resources (e.g., hardware, software, networking, storage, etc.) to run the
            Website and/or Services.<strong>‍</strong>
          </li>
        </ol>
      </section>
      <section>
        <h3>3. Recipient Terms</h3>
        <p>
          If you are a User who receives an entitlement either for a specific product, collection of
          products or for an amount which can be used by you amongst a gift collection for the
          purchase of a product (a “<strong>Gift</strong>”) (each such user, a “
          <strong>Recipient</strong>”) via the Services, the following terms apply to you: <br />
        </p>
        <p>
          Recipient will receive a notice of the Gift via electronic notification from Snappy, and
          Recipient will be able to accept the specific product or replace with a different product
          from the eligible products, or choose a product (if the Gift sender selected a collection
          of products as your Gift). Any Recipient will need to access the Services to claim any
          Gift received.
        </p>
        <p>
          In order to accept the Gift, the Recipient will access the Services. The Product will be
          shipped to the physical or e-mail address Recipient or, in some cases, the Gift sender
          provided us. You acknowledge and agree that a Gift cannot be claimed in cash by the
          Recipient or assigned by the Recipient to a third party.{' '}
        </p>
        <p>
          All Gifts will be delivered in accordance with the applicable retailers’ terms for such
          Gift, and the delivery of the Gift will be handled by a carrier. Snappy does not control
          the delivery time or method of any Gift, and Snappy is not liable for any delivery or
          shipping issues or delays.
        </p>
        <p>
          All Gifts are subject to the terms of the applicable vendor’s terms for such Gift, and
          vendor, and not Snappy, are responsible for the Gifts. All images of Gifts are for
          illustrative purposes only, and Gifts may vary from the pictures. Gifts are subject to
          availability. Although Snappy uses its best efforts to ensure that the availability,
          colors and details of the gift are correct, Snappy does not guarantee that it will match
          in every instance. &nbsp;
        </p>
      </section>
      <section>
        <h3>4. Snappy's Intellectual Property Rights</h3>
        <p>
          All content, visual interfaces, information, graphics, design, compilation, computer code,
          products, software, trademarks, service marks, trade names, and other intellectual
          property rights related thereto, including, but not limited to, the mobile device
          applications, and all other elements contained in the Website and Services (collectively,
          the “<strong>Materials</strong>”) are the property of Snappy or its subsidiaries or
          affiliated companies and/or third-party licensors. These Terms do not convey to you any
          interest in or to the Materials. You have no rights in or to the Materials, and you will
          not use, copy or display the Materials, except as permitted in accordance with these
          Terms. You are granted a limited, revocable right of use for information and personal use
          only in accordance with these Terms. You acknowledge and agree that except for such
          limited right, under no circumstances you shall acquire any right, title or interest to
          any part of the Materials, or any related materials, and that the Materials may not be
          used, reproduced, distributed, transmitted, broadcast, displayed, sold, licensed, or
          otherwise exploited for any other purposes whatsoever. If you send us or otherwise share
          with us, or publicly post on Snappy's social medias, any ideas, comments, or suggestions
          regarding the Materials (the "<strong>Feedback</strong>"), you acknowledge and agree that
          you thereby grant Snappy and its affiliates a worldwide, perpetual, irrevocable,
          royalty-free license to use and incorporate the Feedback in any manner, for any purpose,
          and without the need for any further compensation, approval or consent.
        </p>
        <p>
          We reserve the right to suspend or terminate any Account that has actually or allegedly
          infringed upon the intellectual propriety rights associated with the Materials or any
          third person’s intellectual property rights. In addition, Snappy reserves the right (but
          has no obligation) to pre-screen, review, flag, filter, modify, refuse or remove any or
          all content of an Account from the Website and Services at Snappy’s sole discretion.
        </p>
        <p>
          You agree that Snappy owns all legal rights and intellectual property rights in the
          Website and Services. You must not remove, modify or obscure any legal notices. You are
          not entitled to use Snappy's trademarks, trade names, brands, domain names, or other
          distinctive brand features.
        </p>
        <p>
          With respect to any open source or third-party code that may be incorporated in the
          Website and Services, such open source code is covered by the applicable open source or
          third-party license, if any, authorizing use of such code. Nothing in these Terms limits
          your rights under, or grants you rights that supersede, the terms and conditions of any
          applicable end user license for such open source software.<strong>‍</strong>
        </p>
      </section>
      <section>
        <h3>5. Digital Millennium Copyright Act (“DMCA”) Notice</h3>
        <p>
          Materials may be made available via the Website by third parties not within Snappy’s
          control. Snappy is under no obligation to, and do not, scan content used in connection
          with the Website for the inclusion of illegal or impermissible content. However, Snappy
          respects the copyright interests of others. It is our policy not to permit materials known
          by us to infringe another party’s copyright to remain on the Website.{' '}
        </p>
        <p>
          If you believe any materials on the Website infringe a copyright, you should provide us
          with written notice that at a minimum contains:
        </p>
        <p>
          (i) A physical or electronic signature of a person authorized to act on behalf of the
          owner of an exclusive right that is allegedly infringed;
        </p>
        <p>
          (ii) Identification of the copyrighted work claimed to have been infringed, or, if
          multiple copyrighted works at a single online site are covered by a single notification, a
          representative list of such works at that site;
        </p>
        <p>
          (iii) Identification of the material that is claimed to be infringing or to be the subject
          of infringing activity and that is to be removed or access to which is to be disabled, and
          information reasonably sufficient to permit us to locate the material;
        </p>
        <p>
          (iv) Information reasonably sufficient to permit us to contact the complaining party, such
          as an address, telephone number, and, if available, an electronic mail address at which
          the complaining party may be contacted;
        </p>
        <p>
          (v) A statement that the complaining party has a good faith belief that use of the
          material in the manner complained of is not authorized by the copyright owner, its agent,
          or the law; and
        </p>
        <p>
          (vi) A statement that the information in the notification is accurate, and under penalty
          of perjury, that the complaining party is authorized to act on behalf of the owner of an
          exclusive right that is allegedly infringed.
        </p>
        <p>
          <br />
        </p>
        <p>All DMCA notices should be sent to our designated agent as follows:</p>
        <br />
        <p>Snappy App, Inc.</p>
        <p>Attn: Legal Notices</p>
        <p>33 Irving Place, #5021</p>
        <p>New York, NY 10003</p>
        <p>
          Email: <a href="mailto:legalnotices@snappy.com">legalnotices@snappy.com</a>
        </p>
        <p>
          <br />
        </p>
        <p>
          It is our policy to terminate relationships with parties whose content repeatedly infringe
          the copyrights of others. &nbsp;
        </p>
      </section>
      <section>
        <h3>6. APP Stores - Additional Terms</h3>
        <ol>
          <li>
            The Website and/or Services may be offered via the App, as available on certain app
            stores. &nbsp;You acknowledge that these Terms are between you and us and not with Apple
            Inc. ("Apple") or Google Inc. ("Google"). The Google Play Store or the Apple App Store
            have their own terms and conditions to which you may be required to agree before
            downloading the &nbsp;Website and/or Services from them.
            <br />
          </li>
          <li>
            You agree to comply with, and your license to use the Website and/or Services is
            conditioned upon your compliance with, all applicable terms and conditions of the Google
            Play Store or the Apple App Store, as applicable.
            <br />
          </li>
          <li>
            Please note that the following terms apply in addition to all other provisions in these
            Terms, if you downloaded the App to your mobile device from the Apple App Store or
            Google Play Store.
            <br />
          </li>
          <li>
            You acknowledge and agree that neither Apple nor Google has any responsibility for the
            Website, Services, or Products. Your use of the Website must comply with Apple and
            Google’s store terms and conditions, including Apple’s App Store Terms and Conditions,
            located at{' '}
            <a
              href="http://www.apple.com/legal/internet-services/itunes/us/terms.html"
              target="_blank"
              rel="noreferrer"
            >
              http://www.apple.com/legal/internet-services/itunes/us/terms.html
            </a>{' '}
            and Google’s Google Play Terms of Service, located at
            https://play.google.com/intl/en-us_us/about/play-terms/index.html. You acknowledge that
            Apple and Google have no obligation whatsoever to furnish any maintenance and support
            services with respect to the Website, Services or Products. To the maximum extent
            permitted by applicable law, Apple and Google will have no warranty obligation
            whatsoever with respect to the Website, Services or Products, and any other claims,
            losses, liabilities, damages, costs or expenses attributable to any failure to conform
            to any warranty will be solely governed by these Terms and any law applicable to Snappy
            as provider of the Website. You acknowledge that Apple and Google are not responsible
            for addressing any claims of you or any third party relating to the Website, Services or
            Products or your possession and/or use of the Website, Services, or Products, including,
            but not limited to: (i) product liability claims; (ii) any claim that the iTunes or
            Google Play-sourced software fails to conform to any applicable legal or regulatory
            requirement; and (iii) claims arising under consumer protection or similar legislation;
            and all such claims are governed solely by these Terms and any law applicable to Company
            as provider of the Website. You acknowledge that, in the event of any third party claim
            that the Website or your possession and use of the Website infringes that third party’s
            intellectual property rights, Snappy, not Apple or Google, will be solely responsible
            for the investigation, defense, settlement and discharge of any such intellectual
            property infringement claim to the extent required by these Terms. You and Snappy
            acknowledge and agree that Apple and Google and its and their subsidiaries, are third
            party beneficiaries of this Agreement as relates to your license of the Website, and
            that, upon your acceptance of these Terms, Apple and Google will have the right (and
            will be deemed to have accepted the right) to enforce these Terms as relates to your use
            of the Website against you as a third party beneficiary thereof. &nbsp;You represent
            that you are not located in a country that is subject to a U.S. Government embargo, or
            that has been designated by the U.S. Government as a “terrorist supporting” country; and
            you are not listed on any U.S. Government list of prohibited or restricted parties.
          </li>
        </ol>
      </section>
      <section>
        <h3>7. Mobile Messaging Terms & Conditions</h3>
        <p>
          The Snappy mobile message service (the "Service") is operated by Snappy (“Snappy”, “we”,
          or “us”). Your use of the Service constitutes your agreement to these terms and conditions
          (“Mobile Terms”). We may modify or cancel the Service or any of its features without
          notice. To the extent permitted by applicable law, we may also modify these Mobile Terms
          at any time and your continued use of the Service following the effective date of any such
          changes shall constitute your acceptance of such changes. These Mobile Terms are in
          addition to, and expressly incorporate, the Snappy General Terms of Use (“General Terms”),
          available at{' '}
          <BaseLink href="/terms-general">https://www.snappygifts.com/terms-general</BaseLink>.
        </p>
        <p>
          <strong>
            UNDER THE GENERAL TERMS, YOU ARE AGREEING TO BINDING ARBITRATION AND A CLASS ACTION
            WAIVER. THEY AFFECT YOUR LEGAL RIGHTS. PLEASE READ THE GENERAL TERMS CAREFULLY. THE
            ARBITRATION AGREEMENT AND CLASS ACTION WAIVER REQUIRES US BOTH TO ARBITRATE ALL CLAIMS
            INSTEAD OF GOING TO A COURT BEFORE A JUDGE AND JURY, AND REQUIRES THAT ALL SUCH
            ARBITRATION CLAIMS MUST BE BROUGHT IN YOUR INDIVIDUAL CAPACITY, AND NOT AS CLASS
            REPRESENTATIVE, CLASS MEMBER, OR OTHERWISE ON BEHALF OF OTHERS IN ANY PURPORTED CLASS,
            COLLECTIVE OR REPRESENTATIVE PROCEEDING.
          </strong>
        </p>
        <p>
          By consenting to Snappy SMS/text messaging service, you agree to receive recurring
          SMS/text messages from and on behalf of Snappy through your wireless provider to the
          mobile number you provided, even if your mobile number is registered on any state or
          federal Do Not Call list. Text messages may be sent using an automatic telephone dialing
          system or other technology. Messages may include, but are not limited to, updates, alerts,
          and information (e.g., order updates, account alerts, etc.), promotions, specials, product
          launches, and other marketing offers (e.g., cart reminders).
        </p>
        <p>
          You verify that the mobile number provided to us is true and accurate. You verify that you
          are the current subscriber or owner of any telephone number that you provide. Should any
          of your contact information change, including ownership of your telephone numbers, you
          agree to immediately notify us before the change goes into effect by emailing{' '}
          <a href="mailto:info@snappy.com">info@snappy.com</a>
        </p>
        <p>
          You understand that you do not have to sign up for this program in order to make any
          purchases, and your consent is not a condition of any purchase with Snappy. Your
          participation in this program is completely voluntary.
        </p>
        <p>
          We do not charge for the Service, but you are responsible for all charges and fees
          associated with text messaging imposed by your wireless provider. Message frequency
          varies. Message and data rates may apply. Check your mobile plan and contact your wireless
          provider for details. You are solely responsible for all charges related to SMS/text
          messages, including charges from your wireless provider.
        </p>
        <p>
          You may opt-out of the Service at any time. To opt-out, reply with the single keyword
          command <strong>STOP</strong> to any message you receive from us, the long code
          +18338394015, or click the unsubscribe link (where available) in any text message to
          cancel. We have different text message programs and may use different long codes or
          shortcodes for different messaging purposes. Texting STOP to one long code or opting out
          of one text message program will not effectuate a stop request for all shortcodes or text
          message programs to which you are subscribed. You acknowledge and agree that texting STOP
          to any text message you receive from us will serve as an opt-out of that specified program
          only. It does not serve as a company-wide "do not call" request. You understand and agree
          that you may receive confirmatory opt out text messages after texting{' '}
          <strong>STOP</strong>.
        </p>
        <p>
          For Service support or assistance, text <strong>HELP</strong> to{' '}
          <a href="tel:+18338394015">+18338394015</a> or email{' '}
          <a href="mailto:info@snappy.com">info@snappy.com</a>.
        </p>
        <p>
          We may change any short code or telephone number we use to operate the Service at any time
          and will notify you of these changes. You acknowledge that any messages, including any{' '}
          <strong>STOP</strong> or <strong>HELP</strong> requests, you send to a short code or
          telephone number we have changed may not be received and we will not be responsible for
          honoring requests made in such messages.
        </p>
        <p>
          By participating in the Service, you acknowledge and agree that you are at least eighteen
          (18) years old, or the age of majority in your jurisdiction. You must have a wireless
          device of your own, capable of two-way messaging, be using a participating wireless
          carrier, and be a wireless service subscriber with text messaging service. Not all
          cellular phone providers carry the necessary service to participate and the wireless
          carriers supported by the Service are not liable for delayed or undelivered messages. .
          Check your phone capabilities for specific text messaging instructions.
        </p>
        <p>
          You agree to indemnify us for any privacy, tort or other claims relating to or arising
          from the Service or any of your interactions with Us, including claims under the Federal
          Telephone Consumer Protection Act or any state law equivalents, including claims relating
          to your voluntary provision of a telephone number that is not owned by you and/or your
          failure to notify us of any changes in your mobile telephone number. You agree to
          indemnify, defend and hold us harmless from and against any and all such claims, losses,
          liability, costs and expenses (including reasonable attorneys’ fees).To the extent
          permitted by applicable law, you agree that we will not be liable for failed, delayed, or
          misdirected delivery of any information sent through the Service, any errors in such
          information, and/or any action you may or may not take in reliance on the information or
          Service.
        </p>
        <p>
          We respect your right to privacy. To see how we collect and use your personal information,
          please see our Privacy Policy.
        </p>
      </section>
      <section>
        <h3>8. Privacy</h3>
        <p>
          Please refer to our <BaseLink href="/privacy-policy">Privacy Policy</BaseLink> for
          information about how we collect, use and share personal information about you.
        </p>
      </section>
      <section>
        <h3>9. Interacting With Other Users; Ratings and Review</h3>
        <p>
          You are solely responsible for your interactions with other users of the Services, and
          Snappy is not party to any such disputes. Snappy reserves the right, but has no
          obligation, to monitor disputes between you and other users. You agree to release Snappy,
          its officers, directors, agents, and employees from all claims, demands and damages
          (actual and consequential) of any kind and nature, known and unknown, suspected and
          unsuspected, disclosed and undisclosed, arising out of or in any way connected with any
          disputes between you and any third parties or in any way related to goods, services, or
          events involving third parties.{' '}
        </p>
        <p>
          You agree that in conjunction with your use of the Services you will not inflict emotional
          distress on other people, will not humiliate other people (publicly or otherwise), will
          not assault or threaten other people, will not harass, stalk, intimidate, follow or track
          people or their possessions, will not enter onto private property without permission, will
          not impersonate any other person and will not otherwise engage in any activity that may
          result in injury, death, property damage, and/or liability of any kind.{' '}
        </p>
        <p>
          Snappy may offer you the opportunity to provide ratings and reviews as part of the
          Services. If you undermine the integrity of Snappy’s ratings and/or review system in any
          way, your use of the Services may be suspended or terminated. You acknowledge that Snappy
          does not make judgments on the truth or accuracy of opinions or statements made by you or
          by others. You may only leave one feedback item per unique interaction. You shall not: (i)
          attempt to restrict in any way any other user’s right to post a review; (ii) offer a user
          compensation in exchange for a more favorable review; (iii) gain feedback by receiving
          multiple feedback from the same user; or (iv) misrepresent or impersonate another user.{' '}
        </p>
        <p>
          In order to protect the integrity of its ratings and review system, Snappy will only
          consider removing a feedback item under the following scenarios: (i) if feedback posted
          contains language that is vulgar, discriminatory or contains adult material or otherwise
          violates these Terms; (ii) if Snappy is provided with a court order directing Snappy to
          remove or modify the feedback; or (iii) if feedback contains personally identifiable
          information of any user.{' '}
        </p>
      </section>
      <section>
        <h3>10. Termination and Limitation of the Access to Website and/or Services</h3>
        <p>
          We reserve the right, without notice and in our sole discretion, to terminate or suspend
          your access to or use of all or any part of the Website and/or Services, or to close or
          suspend your Account, effective immediately, at any time including, inter alia, if we
          suspect in our sole discretion that (i) you or your Account were/was engaged or are being
          engaged in fraudulent activity or any illegal activity under any applicable law; (ii) you
          have provided false information to, or concealed any information from, the Snappy or other
          Users; or (iii) you have engaged in activity in violation of these Terms<strong>.</strong>
        </p>
        <p>
          To delete your Account, please send an email to:{' '}
          <a href={`mailto:${EMAILS.SUPPORT}`}>{EMAILS.SUPPORT}</a> with “Account Termination
          Request” in the subject line. The request must be received from the same email that Snappy
          has registered for your Account and should specify the account email and the full name of
          the Account holder. Alternatively, you may delete your Account at any time by accessing
          the settings page and following the instructions therein.
          <br />
        </p>
      </section>
      <section>
        <h3>11. Disclaimer</h3>
        <p>
          The Website and/or Services and any products listed on the Website and/or Services (the
          “Products”), are provided on an “AS-IS” and “AS AVAILABLE” basis without warranties of any
          kind from Snappy.
        </p>
        <p>
          SNAPPY EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, EXPRESS, IMPLIED OR STATUTORY,
          RELATING TO THE WEBSITE, SERVICES, AND PRODUCTS, INCLUDING WITHOUT LIMITATION THE
          WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, QUALITY, OR
          NON-INFRINGEMENT. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, YOU ACKNOWLEDGE AND
          AGREE THAT YOU ASSUME FULL RESPONSIBILITY FOR YOUR USE OF THE WEBSITE, SERVICES AND ANY
          GIFTS AND SNAPPY IS NOT RESPONSIBLE FOR ANY LOSS OR DAMAGE TO PROPERTY OR PERSONAL OR
          BODILY INJURY THAT RESULTS FROM ANY GIFTS OBTAINED VIA THE WEBSITE.
        </p>
        <p>
          Snappy does not make any warranty regarding the quality, completeness, usefulness of the
          Website, Services, or Products or any information in respect of the Website, Services, or
          Products. Snappy does not warrant that the Website, Services, or Products (i) will meet
          your requirements or expectations, (ii) will be delivered uninterrupted, timely, secure,
          or error-free, (iii) will be available, or (iv) the results that may be obtained from the
          use of the Website or Services will be accurate or reliable.
        </p>
        <p>
          Snappy makes no representations or warranties expressed or implied, regarding the legal or
          other consequences resulting from the use of the Website, Services, or Products.
        </p>
        <p>
          Snappy may stop, remove, modify, or add (permanently or temporarily) the Website and/or
          Services at Snappy’s sole discretion. Any new, changed, or removed features are subject to
          the Terms. Continued use of the Website and/or Services after any such changes constitute
          your consent to the changes.
        </p>
      </section>
      <section>
        <h3>12. Links and third party websites</h3>
        <p>
          Portions of the Website and Services involve linking to websites belonging to vendors of
          Products (“<strong>Vendors</strong>”). <br />
        </p>
        <p>
          We have no control over Vendor's or any other third party’s websites, and all use of any
          third party websites is at your own risk. Additionally, Snappy cannot accept
          responsibility for any products or content made available, or payments processed or
          submitted, through such third party websites, nor for their privacy or any other
          practices. Snappy does not endorse any third party websites and we urge our Users to
          exercise caution in using any third party websites.
          <br />
        </p>
      </section>
      <section>
        <h3>13. Limitation of Liability</h3>
        <p>
          IN NO EVENT SNAPPY, OR ANY OF ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, SHALL BE
          LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, CONSEQUENTIAL OR EXEMPLARY
          DAMAGES, INCLUDING BUT NOT LIMITED TO ANY LOSS OF PROFIT, LOSS OF GOODWILL, LOSS OF
          BUSINESS REPUTATION, LOSS OF DATA, COST OF PROCUREMENT OF SUBSTITUTE GOODS OR THE WEBSITE
          OR SERVICES, OR OTHER INTANGIBLE LOSS, RESULTING FROM: (I) THE USE OR THE INABILITY TO USE
          THE WEBSITE OR SERVICES; (II) ANY CHANGES WHICH SNAPPY MAY MAKE TO THE WEBSITE OR
          SERVICES, OR ANY PERMANENT OR TEMPORARY CESSATION IN THE PROVISION OF THE WEBSITE OR
          SERVICES; (III) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA; (IV)
          THE DELETION OF, CORRUPTION OF, OR FAILURE TO STORE, ANY CONTENT AND OTHER COMMUNICATIONS
          DATA MAINTAINED OR TRANSMITTED BY OR THROUGH THE USE OF THE WEBSITE OR SERVICES; (V) THE
          PRODUCTS OR VENDORS, OR (VI) ANY OTHER MATTER RELATING TO THE WEBSITE, PRODUCTS OR
          SERVICES.
          <br />
        </p>
        <p>
          IN ADDITION, IN NO EVENT SNAPPY, OR ANY OF ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS,
          SHALL BE LIABLE FOR ANY DAMAGES WHATSOEVER IN RELATION WITH THE QUALITY, ACCURACY OR
          UTILITY OF THE INFORMATION PROVIDED AS PART OF, OR THROUGH, THE WEBSITE OR SERVICES, OR
          THE PRODUCTS, WHETHER OR NOT THE DAMAGES WERE FORESEEABLE AND WHETHER OR NOT SNAPPY HAS
          BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE FOREGOING LIMITATION OF LIABILITY
          SHALL APPLY TO THE FULLEST EXTENT PERMITTED BY LAW IN ANY APPLICABLE JURISDICTION. &nbsp;
          <br />
        </p>
        <p>
          In addition to the foregoing, we assume no responsibility for any error, omission,
          interruption, deletion, defect, delay in operation or transmission, communications line
          failure, theft or destruction or unauthorized access to, or alteration of, any content. We
          are not responsible for any problems or technical malfunction of any telephone or cellular
          phone network or lines, computer online systems, servers or providers, computer equipment,
          electronic equipment, hardware, software, failure of any email due to technical problems
          or traffic congestion on the Internet or on the Website or Services, including any injury
          or damage to Users or to any person’s mobile device or computer related to or resulting
          from participation or downloading materials in connection with the Website or Services.
          UNDER NO CIRCUMSTANCES SHALL SNAPPY BE RESPONSIBLE FOR ANY LOSS OR DAMAGE, INCLUDING
          PERSONAL INJURY OR DEATH, RESULTING FROM USE OF THE WEBSITE OR SERVICES, OR FROM THE USE
          OF THE PRODUCT, OR FROM ANY CONTENT PROVIDED OR POSTED ON OR THROUGH THE WEBSITE OR
          SERVICES.
          <br />
        </p>
      </section>
      <section>
        <h3>14. Indemnities</h3>
        <p>
          You hereby agree to indemnify and hold Snappy and its affiliates and its and their
          respective licensees, directors, employees, agents and advisors harmless for any claims,
          matters, complaints, costs, liabilities and actions arising out of Snappy’s engagement
          with you in connection to the Website, Services and Products, including but not limited to
          (i) your failure to comply with applicable laws, rules and regulations (ii) any claim of
          infringement or misappropriation upon any User or any third party's rights, including but
          not limited to any privacy rights and/or intellectual property rights (iii) arising from
          your breach of the Terms; or (iv) your use or misuse of the Website or Services. You agree
          to promptly notify Snappy of any third party claims and fully cooperate with Snappy in
          defending such claims at your sole expense. You further agree that Snappy shall have
          control of the defense or settlement of any such claims.
          <br />
        </p>
        <p>
          The abovementioned indemnity clause is in addition to, and is not in any way a substitute
          of any other indemnity that may be agreed upon in a written agreement between you and
          Snappy.
          <br />
        </p>
      </section>
      <section>
        <h3>
          15. Governing law and Dispute settlement; ARBITRATION CLAUSE AND CLASS ACTION
          WAIVER—IMPORTANT—PLEASE REVIEW AS THIS AFFECTS YOUR LEGAL RIGHTS
        </h3>
        <p>
          PLEASE READ THE FOLLOWING SECTION CAREFULLY BECAUSE IT REQUIRES YOU TO ARBITRATE CERTAIN
          DISPUTES AND CLAIMS WITH SNAPPY AND LIMITS THE MANNER IN WHICH YOU CAN SEEK RELIEF FROM
          US. NO CLASS OR REPRESENTATIVE ACTIONS OR ARBITRATIONS ARE ALLOWED UNDER THIS ARBITRATION
          AGREEMENT. IN ADDITION, ARBITRATION PRECLUDES YOU FROM SUING IN COURT OR HAVING A JURY
          TRIAL.
        </p>
        <p>
          These Terms shall be governed by, and interpreted in accordance with, the law of New York
          exclusive of its choice of law rules. Your conduct may also be subject to other local,
          state, and national laws. In the event of a dispute arising under or relating to these
          Terms, the Website, Services or any other products or services provided by us (each, a “
          <strong>Dispute</strong>”), such Dispute will be finally and exclusively resolved by
          binding arbitration governed by the Federal Arbitration Act (“FAA”). YOU AND WE AGREE THAT
          NEITHER YOU OR US HAVE THE RIGHT TO LITIGATE SUCH DISPUTE IN COURT BEFORE A JUDGE OR JURY,
          EXCEPT FOR SMALL CLAIMS DISPUTES IN WHICH YOU OR SNAPPY SEEK TO BRING AN INDIVIDUAL ACTION
          IN SMALL CLAIMS COURT LOCATED IN THE COUNTY OF YOUR BILLING ADDRESS OR DISPUTES IN WHICH
          SNAPPY SEEKS INJUNCTIVE OR OTHER EQUITABLE RELIEF FOR THE ALLEGED UNLAWFUL USE OF
          INTELLECTUAL PROPERTY. All Disputes will be resolved before a single, neutral arbitrator
          located in New York, New York, whose decision will be final except for a limited right of
          appeal under the FAA. &nbsp;The arbitration shall be commenced and conducted by the
          Judicial Arbitration and Mediation Services (“JAMS”) pursuant to its then current
          Comprehensive Arbitration Rules and Procedures and in accordance with the Expedited
          Procedures in those rules, or, where appropriate, pursuant to JAMS’ Streamlined
          Arbitration Rules and Procedures. All applicable JAMS’ rules and procedures are available
          at the
          <a href="http://www.jamsadr.com/" target="_blank" rel="noreferrer">
            {' '}
            JAMS website
          </a>
          . Each party will be responsible for paying any JAMS filing, administrative and arbitrator
          fees in accordance with JAMS rules. You acknowledge and agree that you either have read
          and understand the JAMS Rules or waive your opportunity to read the JAMS Rules and waive
          any claim that the JAMS Rules are unfair or should not apply for any reason.
        </p>
        <p>
          The arbitrator, Snappy, and you, will maintain the confidentiality of any arbitration
          proceedings, judgments and awards, including, but not limited to, all information
          gathered, prepared and presented for purposes of the arbitration or related to the
          dispute(s) therein. The arbitrator will have the authority to make appropriate rulings to
          safeguard confidentiality, unless the law provides to the contrary. The duty of
          confidentiality does not apply to the extent that disclosure is necessary to prepare for
          or conduct the arbitration hearing on the merits, in connection with a court application
          for a preliminary remedy or in connection with a judicial challenge to an arbitration
          award or its enforcement, or to the extent that disclosure is otherwise required by law or
          judicial decision.
        </p>
        <p>
          You and Snappy agree that the state or federal courts of the State of New York and the
          United States sitting in the county of New York in the State of New York, have exclusive
          jurisdiction over any appeals that are not subject to arbitration as set forth above and
          the enforcement of an arbitration award or in the event the agreement to arbitrate above
          is found not to apply to you or to a particular claim or dispute. You agree to submit to
          the exclusive personal jurisdiction of the courts sitting within the county of New York in
          the State of New York for the purpose of litigating all such claims or disputes.
        </p>
        <p>
          This clause shall not preclude the parties from seeking provisional remedies in aid of
          arbitration from a court of appropriate jurisdiction. The arbitration may be conducted
          pursuant to JAMS rules in person, through the submission of documents, or utilizing desk,
          phone or video conference proceedings where appropriate and permitted to mitigate costs of
          travel. You and Snappy may litigate in court to compel arbitration, to stay a proceeding
          pending arbitration, or to confirm, modify, vacate or enter judgment on the award entered
          by the arbitrator. &nbsp;You and Snappy shall cooperate in good faith in the voluntary and
          informal exchange of all non-privileged documents and other information (including
          electronically stored information) relevant to the Dispute immediately after commencement
          of the arbitration. Nothing in these Terms will prevent Snappy from seeking injunctive
          relief in any court of competent jurisdiction as necessary to protect Snappy’s proprietary
          interests. Any dispute between you and Snappy that is not subject to arbitration or cannot
          be heard in small claims court will be resolved in state and federal courts sitting in the
          City of New York in the State of New York.<strong>‍</strong>
        </p>
        <p>
          <strong>
            Any cause of action arising out of relating to the Website or Services against Snappy
            must be brought within one (1) year of the date such cause of action arose, otherwise,
            the claim is permanently barred, which means that you and Snappy will not have the right
            to assert the claim.{' '}
          </strong>
        </p>
        <p>
          <strong>
            YOU AND SNAPPY AGREE THAT ANY DISPUTE ARISING OR RELATED TO THE WEBSITE OR SERVICES AND
            ALL CLAIMS BROUGHT AGAINST THE OTHER MUST BE BROUGHT IN SUCH PARTY’S INDIVIDUAL
            CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION,
            COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION OR OTHER REPRESENTATIVE PROCEEDING,
            EXCEPT TO THE EXTENT SUCH RESTRICTION IS PROHIBITED BY APPLICABLE LAW.{' '}
          </strong>
          ‍
        </p>
      </section>
      <section>
        <h3>
          16. <strong>Miscellaneous</strong>‍
        </h3>
        <p>
          In the event that any provision of these Terms is held to be unenforceable, such provision
          shall be replaced with an enforceable provision, which most closely achieves the effect of
          the original provision, and the remaining terms of these Terms shall remain in full force
          and effect.{' '}
        </p>
        <p>
          Nothing in these Terms creates any agency, employment, joint venture, or partnership
          relationship between you and Snappy or authorizes you to act on behalf of Snappy.{' '}
        </p>
        <p>
          We may assign our rights and obligations hereunder to any third party without prior
          notice. You shall not assign any of your rights or obligations hereunder, and any
          assignment in violation of the foregoing shall be void.{' '}
        </p>
        <p>
          No waiver of any breach or default hereunder shall be deemed to be a waiver of any
          preceding or subsequent breach or default.
        </p>
        <p>
          All provisions of these Terms which by their nature should survive termination shall
          survive termination, including, without limitation, ownership provisions, warranty
          disclaimers, indemnity and limitations of liability.
          <br />
        </p>
      </section>
      <section>
        <h3>17. How to Contact us?</h3>
        <p>
          The best way to get in touch with is to contact us at:{' '}
          <a href={`mailto:${EMAILS.SUPPORT}`}>{EMAILS.SUPPORT}</a>. &nbsp;We'd love to hear your
          feedback about the Website and Services.
        </p>
      </section>
    </article>
  </LegalPageWrapper>
);

export default TermsGeneral;
